import * as React from 'react';

import { Devices, type IDevice, MinScreenSizes } from '~/vendor/cx-web/domain-types';
import { CookieHelper } from '~/vendor/cx-web/feature-cookies';

// Single source of truth for client device detection. Mounted once, by UIConfigProvider,
// it owns exactly one resize listener and one device-cookie writer regardless of how many components
// call useMedia. The SSR/hydration model is preserved: the server seed (deviceType) is used until the
// client mounts, then the post-mount client measurement takes over (device = clientDevice ?? serverDevice).

export type MediaContextData = {
  device: IDevice;
  isDesktop: boolean;
  isMobile: boolean;
  isTablet: boolean;
};

export const MediaContext = React.createContext<MediaContextData | undefined>(undefined);
MediaContext.displayName = 'MediaContext';

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

function resolveDevice(width: number): IDevice {
  const { desktop, tablet } = MinScreenSizes;
  if (width < tablet) return Devices.mobile;
  if (width < desktop) return Devices.tablet;
  return Devices.desktop;
}

function normalizeServerDevice(deviceType: string): IDevice {
  const key = deviceType.toLowerCase();
  return key in Devices ? (key as IDevice) : Devices.desktop;
}

export const MediaProvider = ({ children, deviceType }: React.PropsWithChildren<{ deviceType: string }>) => {
  const serverDevice = normalizeServerDevice(deviceType);
  // useRef (not useMemo) for a guaranteed-stable instance: useMemo may be discarded and recomputed
  // (e.g. StrictMode), and a fresh CookieHelper in the effect dep array would trigger a spurious cookie write.
  const cookieHelper = React.useRef(new CookieHelper()).current;

  const [clientDevice, setClientDevice] = React.useState<IDevice>();

  React.useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (!width) return;
      // Same-bucket updates are a no-op: React bails on equal primitive state, so this only
      // re-renders (and only triggers the cookie write below) when the device actually changes.
      setClientDevice(resolveDevice(width));
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    if (!clientDevice) return;
    cookieHelper.setBrowserCookie('device', clientDevice, new Date().getTime() + ONE_YEAR_MS);
  }, [clientDevice, cookieHelper]);

  const contextValue = React.useMemo<MediaContextData>(() => {
    const device = clientDevice ?? serverDevice;
    return {
      device,
      isDesktop: device === Devices.desktop,
      isMobile: device === Devices.mobile,
      isTablet: device === Devices.tablet,
    };
  }, [clientDevice, serverDevice]);

  return <MediaContext.Provider value={contextValue}>{children}</MediaContext.Provider>;
};
MediaProvider.displayName = 'MediaProvider';
